"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.addValueSetsToMeasureBundle = exports.extractMeasureFromBundle = exports.extractLibrariesFromMeasureBundle = exports.extractLibrariesFromBundle = exports.parseLibRef = exports.extractLibrariesFromLibraryBundle = exports.isValidLibraryURL = exports.extractMeasurementPeriod = exports.codeableConceptToPopulationType = exports.getObservationResultForPopulation = exports.getRelevantIPPFromPopulation = exports.hasMultipleIPPs = exports.getCriteriaReferenceIdFromPopulation = exports.isEpisodeOfCareGroup = exports.isEpisodeOfCareMeasure = exports.isCVMeasure = exports.isRatioMeasure = exports.filterComponentResults = exports.getWeightForComponent = exports.getGroupIdForComponent = exports.extractCompositeMeasure = exports.extractComponentsFromMeasure = exports.getCompositeScoringFromMeasure = exports.getScoringCodeFromMeasure = exports.getScoringCodeFromGroup = exports.getImprovementNotationFromGroup = void 0;
const Enums_1 = require("../types/Enums");
const CustomErrors_1 = require("../types/errors/CustomErrors");
const ValueSetHelper_1 = require("../execution/ValueSetHelper");
const ValueSetResolver_1 = require("../execution/ValueSetResolver");
/**
 * The extension that defines the population basis. This is used to determine if the measure is an episode of care or
 * patient based measure.
 */
const POPULATION_BASIS_EXT = 'http://hl7.org/fhir/us/cqfmeasures/StructureDefinition/cqfm-populationBasis';
const SCORING_CODE_EXT = 'http://hl7.org/fhir/us/cqfmeasures/StructureDefinition/cqfm-scoring';
const IMPROVEMENT_NOTATION_EXT = 'http://hl7.org/fhir/us/cqfmeasures/StructureDefinition/cqfm-improvementNotation';
function getImprovementNotationFromGroup(group) {
    var _a, _b, _c, _d, _e;
    return ((_e = (_d = (_c = (_b = (_a = group === null || group === void 0 ? void 0 : group.extension) === null || _a === void 0 ? void 0 : _a.find(ext => ext.url === IMPROVEMENT_NOTATION_EXT)) === null || _b === void 0 ? void 0 : _b.valueCodeableConcept) === null || _c === void 0 ? void 0 : _c.coding) === null || _d === void 0 ? void 0 : _d[0].code) !== null && _e !== void 0 ? _e : null);
}
exports.getImprovementNotationFromGroup = getImprovementNotationFromGroup;
function getScoringCodeFromGroup(group) {
    var _a, _b, _c, _d, _e;
    return (_e = (_d = (_c = (_b = (_a = group === null || group === void 0 ? void 0 : group.extension) === null || _a === void 0 ? void 0 : _a.find(ext => ext.url === SCORING_CODE_EXT)) === null || _b === void 0 ? void 0 : _b.valueCodeableConcept) === null || _c === void 0 ? void 0 : _c.coding) === null || _d === void 0 ? void 0 : _d[0].code) !== null && _e !== void 0 ? _e : null;
}
exports.getScoringCodeFromGroup = getScoringCodeFromGroup;
function getScoringCodeFromMeasure(measure) {
    var _a, _b, _c;
    return (_c = (_b = (_a = measure.scoring) === null || _a === void 0 ? void 0 : _a.coding) === null || _b === void 0 ? void 0 : _b.find(c => c.system === 'http://hl7.org/fhir/measure-scoring' ||
        c.system === 'http://terminology.hl7.org/CodeSystem/measure-scoring')) === null || _c === void 0 ? void 0 : _c.code;
}
exports.getScoringCodeFromMeasure = getScoringCodeFromMeasure;
function getCompositeScoringFromMeasure(measure) {
    var _a, _b, _c;
    return (_c = (_b = (_a = measure.compositeScoring) === null || _a === void 0 ? void 0 : _a.coding) === null || _b === void 0 ? void 0 : _b.find(c => c.system === 'http://terminology.hl7.org/CodeSystem/composite-measure-scoring')) === null || _c === void 0 ? void 0 : _c.code;
}
exports.getCompositeScoringFromMeasure = getCompositeScoringFromMeasure;
/**
 * Extracts all component measures that are defined on the relatedArtifact of a composite measure.
 * @param compositeMeasureResource composite measure resource
 * @param measureBundle FHIR Measure Bundle
 * @returns array of measures with libraries included
 */
function extractComponentsFromMeasure(compositeMeasureResource, measureBundle) {
    var _a, _b, _c;
    const componentRefs = (_a = compositeMeasureResource.relatedArtifact) === null || _a === void 0 ? void 0 : _a.filter(ra => ra.type === 'composed-of');
    if (componentRefs == null || componentRefs.length < 2) {
        throw new Error('Composite measures must specify at least two components');
    }
    const uniqueCanonicalsFromComposite = new Set(componentRefs.map(ra => ra.resource));
    const allMeasuresInBundle = (_c = (_b = measureBundle.entry) === null || _b === void 0 ? void 0 : _b.filter(e => { var _a; 
    // Ensure that only measures with logic libraries should be considered
    return ((_a = e.resource) === null || _a === void 0 ? void 0 : _a.resourceType) === 'Measure' && e.resource.url !== compositeMeasureResource.url; }).map(e => e.resource)) !== null && _c !== void 0 ? _c : [];
    const uniqueCanonicalsInBundle = new Set(allMeasuresInBundle.map(m => `${m.url}${m.version ? `|${m.version}` : ''}`));
    const missingCanonicalsInBundle = new Set([...uniqueCanonicalsFromComposite].filter(c => !uniqueCanonicalsInBundle.has(c)));
    if (missingCanonicalsInBundle.size > 0) {
        throw new Error(`Missing components from measure bundle: "${[...missingCanonicalsInBundle].join(', ')}"`);
    }
    return allMeasuresInBundle.filter(measure => {
        if (!measure.library) {
            throw new CustomErrors_1.UnexpectedProperty(`Measure resource "Measure/${measure.id}" must specify a "library"`);
        }
        if (!measure.url)
            return false;
        if (uniqueCanonicalsFromComposite.has(measure.url)) {
            return true;
        }
        if (measure.version) {
            return uniqueCanonicalsFromComposite.has(`${measure.url}|${measure.version}`);
        }
        return false;
    });
}
exports.extractComponentsFromMeasure = extractComponentsFromMeasure;
function extractCompositeMeasure(measureBundle) {
    var _a, _b;
    const allCompositeMeasures = (_b = (_a = measureBundle.entry) === null || _a === void 0 ? void 0 : _a.filter(e => { var _a; return ((_a = e.resource) === null || _a === void 0 ? void 0 : _a.resourceType) === 'Measure' && getScoringCodeFromMeasure(e.resource) === 'composite'; })) === null || _b === void 0 ? void 0 : _b.map(e => e.resource);
    if (!allCompositeMeasures || allCompositeMeasures.length === 0) {
        return undefined;
    }
    if (allCompositeMeasures.length > 1) {
        throw new Error('Composite measure calculation must only include one composite Measure resource in the measure bundle');
    }
    return allCompositeMeasures[0];
}
exports.extractCompositeMeasure = extractCompositeMeasure;
/**
 * Extracts CQFM Group Id from a given composite measure Related Artifact.
 * https://build.fhir.org/ig/HL7/cqf-measures/StructureDefinition-cqfm-groupId.html
 * @param relatedArtifact related artifact defined on the composite measure
 * @returns group id, if defined
 */
function getGroupIdForComponent(relatedArtifact) {
    var _a, _b, _c;
    const groupIdExtension = (_a = relatedArtifact.extension) === null || _a === void 0 ? void 0 : _a.filter(({ url }) => url === 'http://hl7.org/fhir/us/cqfmeasures/StructureDefinition/cqfm-groupId');
    if (groupIdExtension && groupIdExtension.length > 1) {
        throw new Error(`Only one CQFM Group Id extension can be defined on a component, but ${groupIdExtension.length} were provided.`);
    }
    return (_c = (_b = groupIdExtension === null || groupIdExtension === void 0 ? void 0 : groupIdExtension[0]) === null || _b === void 0 ? void 0 : _b.valueString) !== null && _c !== void 0 ? _c : null;
}
exports.getGroupIdForComponent = getGroupIdForComponent;
/**
 * Extracts CQFM Weight from a given composite measure Related Artifact.
 * https://build.fhir.org/ig/HL7/cqf-measures/StructureDefinition-cqfm-weight.html
 * @param relatedArtifact related artifact defined on the composite measure
 * @returns weight extension value, if defined
 */
function getWeightForComponent(relatedArtifact) {
    var _a, _b, _c;
    const weightExtension = (_a = relatedArtifact.extension) === null || _a === void 0 ? void 0 : _a.filter(({ url }) => url === 'http://hl7.org/fhir/us/cqfmeasures/StructureDefinition/cqfm-weight');
    if (weightExtension && weightExtension.length > 1) {
        throw new Error(`Only one CQFM Weight extension can be defined on a component, but ${weightExtension.length} were provided.`);
    }
    return (_c = (_b = weightExtension === null || weightExtension === void 0 ? void 0 : weightExtension[0]) === null || _b === void 0 ? void 0 : _b.valueDecimal) !== null && _c !== void 0 ? _c : null;
}
exports.getWeightForComponent = getWeightForComponent;
/**
 * Filters component results using mapping of group ids defined by the CQFM Group Id extension on the
 * composite measure. Throws error if a component contains multiple groups but no group is specified to
 * be used for measure score calculation.
 * @param componentGroupIds mapping of components to group ids defined by the CQFM Group Id extension on the composite
 * @param componentResults array of component results from detailed results
 * @returns component results, filtered by desired group Ids
 */
function filterComponentResults(componentGroupIds, componentResults) {
    var _a;
    const filteredComponentResults = componentResults === null || componentResults === void 0 ? void 0 : componentResults.filter(cr => {
        // keep component result if its group matches the group defined on the group Id extension
        if (cr.componentCanonical &&
            typeof componentGroupIds[cr.componentCanonical] === 'object' &&
            Object.keys(componentGroupIds[cr.componentCanonical]).includes(cr.groupId)) {
            return true;
        }
        // keep component result if only one group is defined for the given component
        else if (cr.componentCanonical &&
            typeof componentGroupIds[cr.componentCanonical] === 'number' &&
            componentResults.filter(c => c.componentCanonical === cr.componentCanonical).length === 1) {
            return true;
        }
        else {
            // throw error if no group Id is defined for the component
            if (cr.componentCanonical &&
                componentGroupIds[cr.componentCanonical] &&
                typeof componentGroupIds[cr.componentCanonical] === 'number' &&
                componentResults.filter(c => c.componentCanonical === cr.componentCanonical).length > 1) {
                throw new Error('For component measures that contain multiple population groups, the composite measure SHALL specify a specific group, but no group was specified.');
            }
        }
    });
    // throw error if defined group Id does not correspond to a group on the component
    for (const key in componentGroupIds) {
        const definedGroupIds = (_a = componentResults === null || componentResults === void 0 ? void 0 : componentResults.filter(cr => cr.componentCanonical === key).map(cr => cr.groupId)) !== null && _a !== void 0 ? _a : [];
        if (typeof componentGroupIds[key] === 'object') {
            Object.keys(componentGroupIds[key]).forEach(groupId => {
                if (!definedGroupIds.includes(groupId)) {
                    throw new Error('For component measures that contain multiple population groups, the composite measure SHALL specify a specific group. The specified group does not exist.');
                }
            });
        }
    }
    return filteredComponentResults !== null && filteredComponentResults !== void 0 ? filteredComponentResults : [];
}
exports.filterComponentResults = filterComponentResults;
/**
 * Checks if a given measure/measure group has scoring code 'ratio.'
 * @param group measure group (used to extract scoring code if present on the group)
 * @param measureScoringCode scoring code for measure (used if scoring code not provided at the group level)
 * @returns true if scoring code is 'ratio' for the group or at the measure root, false otherwise
 */
function isRatioMeasure(group, measureScoringCode) {
    return getScoringCodeFromGroup(group) === Enums_1.MeasureScoreType.RATIO || measureScoringCode === Enums_1.MeasureScoreType.RATIO;
}
exports.isRatioMeasure = isRatioMeasure;
/**
 * Checks if a given measure/measure group has scoring code 'cv' (continuous variable)
 * @param group measure group (used to extract scoring code if present on the group)
 * @param measureScoringCode scoring code for measure (used if scoring code not provided at the group level)
 * @returns true if scoring code is 'cv' for the group or at the measure root, false otherwise
 */
function isCVMeasure(group, measureScoringCode) {
    return getScoringCodeFromGroup(group) === Enums_1.MeasureScoreType.CV || measureScoringCode === Enums_1.MeasureScoreType.CV;
}
exports.isCVMeasure = isCVMeasure;
/**
 * Check if a measure is an episode of care measure or not. Look for the cqfm-populationBasis extension.
 * If it is found return true if valueCode is not 'boolean' otherwise return false.
 *
 * @param {fhir4.Measure} measure FHIR Measure resource.
 * @returns {boolean} true if this is an episode of care, false if it is a patient measure.
 */
function isEpisodeOfCareMeasure(measure) {
    var _a;
    const popBasisExt = (_a = measure.extension) === null || _a === void 0 ? void 0 : _a.find(ext => ext.url == POPULATION_BASIS_EXT);
    if (popBasisExt != undefined) {
        return popBasisExt.valueCode !== 'boolean';
    }
    else {
        return false;
    }
}
exports.isEpisodeOfCareMeasure = isEpisodeOfCareMeasure;
/**
 * Check if a group is an episode of care group or not. Look for the cqfm-populationBasis extension.
 * If it is found return true if valueCode is not 'boolean'. If the extension cannot be found, fallback
 * to looking at the measure.
 *
 * @param {fhir4.Measure} measure FHIR Measure resource.
 * @returns {boolean} true if this is an episode of care, false if it is a patient measure.
 */
function isEpisodeOfCareGroup(measure, group) {
    var _a, _b;
    const popBasisExt = (_a = group.extension) === null || _a === void 0 ? void 0 : _a.find(ext => ext.url == POPULATION_BASIS_EXT);
    if (popBasisExt != undefined) {
        return popBasisExt.valueCode !== 'boolean';
    }
    else if (getScoringCodeFromGroup(group) === Enums_1.MeasureScoreType.RATIO ||
        getScoringCodeFromMeasure(measure) === Enums_1.MeasureScoreType.RATIO) {
        const populationsWithBasis = (_b = group.population) === null || _b === void 0 ? void 0 : _b.filter(p => {
            var _a;
            return codeableConceptToPopulationType(p.code) !== Enums_1.PopulationType.OBSERV &&
                ((_a = p.extension) === null || _a === void 0 ? void 0 : _a.find(ext => ext.url === POPULATION_BASIS_EXT)) != null;
        });
        if (populationsWithBasis && populationsWithBasis.length > 0) {
            return populationsWithBasis.some(p => { var _a, _b; return ((_b = (_a = p.extension) === null || _a === void 0 ? void 0 : _a.find(ext => ext.url === POPULATION_BASIS_EXT)) === null || _b === void 0 ? void 0 : _b.valueCode) !== 'boolean'; });
        }
        return isEpisodeOfCareMeasure(measure);
    }
    else {
        return isEpisodeOfCareMeasure(measure);
    }
}
exports.isEpisodeOfCareGroup = isEpisodeOfCareGroup;
/**
 * Measure Observation populations can specify which population in the measure they are drawing observations from (e.g. Numerator/Denominator/etc.)
 * This is done by referencing the ID of that population in the valueString of the cqfm-criteriaReference extension
 * This function identifies the ID used in one of those measure observation populations
 *
 * @param population within a Measure resource that may reference another population within the same Measure
 * @returns the corresponding ID from the extension used in the population, or null if none is found
 */
function getCriteriaReferenceIdFromPopulation(population) {
    var _a, _b, _c;
    return ((_c = (_b = (_a = population.extension) === null || _a === void 0 ? void 0 : _a.find(e => e.url === 'http://hl7.org/fhir/us/cqfmeasures/StructureDefinition/cqfm-criteriaReference')) === null || _b === void 0 ? void 0 : _b.valueString) !== null && _c !== void 0 ? _c : null);
}
exports.getCriteriaReferenceIdFromPopulation = getCriteriaReferenceIdFromPopulation;
function hasMultipleIPPs(group) {
    var _a, _b;
    return (((_b = (_a = group.population) === null || _a === void 0 ? void 0 : _a.filter(p => codeableConceptToPopulationType(p.code) === Enums_1.PopulationType.IPP)) !== null && _b !== void 0 ? _b : []).length > 1);
}
exports.hasMultipleIPPs = hasMultipleIPPs;
/**
 * Uses the criteriaReference extension for a given population to look up which IPP it draws from
 * This is useful in the case of multiple IPPs in ratio measures, as the numerator and denominator can each draw from
 * different IPPs
 */
function getRelevantIPPFromPopulation(group, fromPopulationType) {
    var _a, _b, _c;
    const fromPopulation = (_a = group.population) === null || _a === void 0 ? void 0 : _a.find(p => codeableConceptToPopulationType(p.code) === fromPopulationType);
    if (fromPopulation == null) {
        return null;
    }
    const ippId = getCriteriaReferenceIdFromPopulation(fromPopulation);
    if (ippId == null) {
        return null;
    }
    return (_c = (_b = group.population) === null || _b === void 0 ? void 0 : _b.find(p => p.id === ippId)) !== null && _c !== void 0 ? _c : null;
}
exports.getRelevantIPPFromPopulation = getRelevantIPPFromPopulation;
/**
 * Finds the measure observation that references the desired population in its criteria reference
 * and returns its populationResult
 */
function getObservationResultForPopulation(group, popResults, desiredPopulationType) {
    var _a, _b, _c, _d, _e;
    const popId = (_b = (_a = group === null || group === void 0 ? void 0 : group.population) === null || _a === void 0 ? void 0 : _a.find(pop => codeableConceptToPopulationType(pop.code) === desiredPopulationType)) === null || _b === void 0 ? void 0 : _b.id;
    if (popId) {
        const desiredObservation = (_c = group === null || group === void 0 ? void 0 : group.population) === null || _c === void 0 ? void 0 : _c.find(pop => {
            return (codeableConceptToPopulationType(pop.code) === Enums_1.PopulationType.OBSERV &&
                getCriteriaReferenceIdFromPopulation(pop) === popId);
        });
        const criteriaCode = (_d = desiredObservation === null || desiredObservation === void 0 ? void 0 : desiredObservation.criteria) === null || _d === void 0 ? void 0 : _d.expression;
        if (criteriaCode && desiredObservation) {
            return ((_e = popResults.find(e => e.criteriaExpression === criteriaCode && (e.populationId ? e.populationId === desiredObservation.id : true))) !== null && _e !== void 0 ? _e : null);
        }
    }
    return null;
}
exports.getObservationResultForPopulation = getObservationResultForPopulation;
/**
 * Population Type Code system.
 */
const POPULATION_TYPE_CODESYSTEM = 'http://terminology.hl7.org/CodeSystem/measure-population';
/**
 * Converts FHIR CodeableConcept value for the measure population type to a PopulationType enum value.
 *
 * @param {fhir4.CodeableConcept|undefined} concept The FHIR CodeableConcept value for the measure population.
 * @returns {PopulationType|null} null if not a proper population type. The PopulationType if it is.
 */
function codeableConceptToPopulationType(concept) {
    var _a;
    const populationTypeCoding = (_a = concept === null || concept === void 0 ? void 0 : concept.coding) === null || _a === void 0 ? void 0 : _a.find(coding => {
        return coding.system == POPULATION_TYPE_CODESYSTEM;
    });
    if ((populationTypeCoding === null || populationTypeCoding === void 0 ? void 0 : populationTypeCoding.code) != null && Object.values(Enums_1.PopulationType).includes(populationTypeCoding.code)) {
        return populationTypeCoding.code;
    }
    return null;
}
exports.codeableConceptToPopulationType = codeableConceptToPopulationType;
/**
 * Pulls the measurement period out of the provided FHIR Measure resource, assuming one is set.
 * NOTE: the default start/end values are also set in Execution.ts
 * so if this date is changed from 2019 it must also be changed there
 *
 * @param measure FHIR Measure resource
 * @returns {CalculationOptions} object with only the measurement period start/end fields filled out,
 * or the year 2019 set as the calculation period if not set in the Measure.
 */
function extractMeasurementPeriod(measure) {
    var _a, _b;
    return {
        measurementPeriodStart: ((_a = measure.effectivePeriod) === null || _a === void 0 ? void 0 : _a.start) || '2019-01-01',
        measurementPeriodEnd: ((_b = measure.effectivePeriod) === null || _b === void 0 ? void 0 : _b.end) || '2019-12-31'
    };
}
exports.extractMeasurementPeriod = extractMeasurementPeriod;
/**
 * Figure out if a string is a  valid URL  or if it's just a string
 * @public
 * @param {string} libraryName - The name of the library depending on the origin of the measure this may a url
 * @return {boolean} If the statement is a function or not.
 */
function isValidLibraryURL(libraryName) {
    const urlFormat = /(ftp|http|https):\/\/(\w+:{0,1}\w*@)?(\S+)(:[0-9]+)?(\/|\/([\w#!:.?+=&%@!\-\/]))?/;
    const r = new RegExp(urlFormat);
    return r.test(libraryName);
}
exports.isValidLibraryURL = isValidLibraryURL;
/**
 * Returns the cqls, rootLibIdentifier, and elmJSONs for a collection of libraries
 * within a Library Bundle
 */
function extractLibrariesFromLibraryBundle(libraryBundle, rootLibRef) {
    const { libId: rootLibId, libVersion: rootLibVersion } = parseLibRef(rootLibRef);
    const { cqls, rootLibIdentifier, elmJSONs } = extractLibrariesFromBundle(libraryBundle, rootLibId, rootLibVersion);
    if (rootLibIdentifier.id === '') {
        throw new CustomErrors_1.UnexpectedResource('No Root Library could be identified in provided library bundle');
    }
    return { cqls, rootLibIdentifier, elmJSONs };
}
exports.extractLibrariesFromLibraryBundle = extractLibrariesFromLibraryBundle;
/**
 * Parses a library reference as canonical url or Library/id to an id and version.
 *
 * @param rootLibRef
 * @returns Library id and version
 */
function parseLibRef(libRef) {
    let libId;
    let libVersion;
    if (isValidLibraryURL(libRef)) {
        if (libRef.includes('|')) {
            const splitLibRef = libRef.split('|');
            libId = splitLibRef[0];
            libVersion = splitLibRef[1];
        }
        else {
            libId = libRef;
        }
    }
    else
        libId = libRef.substring(libRef.indexOf('/') + 1);
    return { libId, libVersion };
}
exports.parseLibRef = parseLibRef;
/**
 * Returns the cqls, rootLibIdentifier, and elmJSONs for a collection of libraries
 * within a Bundle
 */
function extractLibrariesFromBundle(bundle, rootLibId, rootLibVersion) {
    var _a;
    const libraries = [];
    const elmJSONs = [];
    const cqls = [];
    let rootLibIdentifier = {
        id: '',
        version: ''
    };
    (_a = bundle.entry) === null || _a === void 0 ? void 0 : _a.forEach(e => {
        var _a, _b, _c;
        if (((_a = e.resource) === null || _a === void 0 ? void 0 : _a.resourceType) == 'Library') {
            const library = e.resource;
            libraries.push(library);
            const elmsEncoded = (_b = library.content) === null || _b === void 0 ? void 0 : _b.filter(a => a.contentType === 'application/elm+json');
            elmsEncoded === null || elmsEncoded === void 0 ? void 0 : elmsEncoded.forEach(elmEncoded => {
                var _a, _b;
                if (elmEncoded.data) {
                    const decoded = Buffer.from(elmEncoded.data, 'base64').toString('binary');
                    const elm = JSON.parse(decoded);
                    // If url matches and we have a defined rootLibVersion, check that version matches also
                    if (library.url === rootLibId && (!rootLibVersion || library.version === rootLibVersion)) {
                        rootLibIdentifier = elm.library.identifier;
                    }
                    else if (library.id === rootLibId) {
                        rootLibIdentifier = elm.library.identifier;
                    }
                    if ((_b = (_a = elm.library) === null || _a === void 0 ? void 0 : _a.includes) === null || _b === void 0 ? void 0 : _b.def) {
                        elm.library.includes.def = elm.library.includes.def.map(def => {
                            def.path = def.path.substring(def.path.lastIndexOf('/') + 1);
                            return def;
                        });
                    }
                    elmJSONs.push(elm);
                }
            });
            const cqlsEncoded = (_c = library.content) === null || _c === void 0 ? void 0 : _c.filter(a => a.contentType === 'text/cql');
            cqlsEncoded === null || cqlsEncoded === void 0 ? void 0 : cqlsEncoded.forEach(elmEncoded => {
                if (elmEncoded.data) {
                    const decoded = Buffer.from(elmEncoded.data, 'base64').toString('binary');
                    const cql = {
                        name: library.name || library.id || 'unknown library',
                        cql: decoded
                    };
                    cqls.push(cql);
                }
            });
        }
    });
    return { cqls, rootLibIdentifier, elmJSONs };
}
exports.extractLibrariesFromBundle = extractLibrariesFromBundle;
/**
 * Returns the cqls, rootLibIdentifier, and elmJSONs for a collection of libraries
 * within a Measure Bundle
 */
function extractLibrariesFromMeasureBundle(measureBundle, measure) {
    const rootLibRef = measure ? measure.library[0] : extractMeasureFromBundle(measureBundle).library[0];
    let rootLibId;
    if (isValidLibraryURL(rootLibRef))
        rootLibId = rootLibRef;
    else
        rootLibId = rootLibRef.substring(rootLibRef.indexOf('/') + 1);
    const { cqls, rootLibIdentifier, elmJSONs } = extractLibrariesFromBundle(measureBundle, rootLibId);
    if (rootLibIdentifier.id === '') {
        throw new CustomErrors_1.UnexpectedResource('No Root Library could be identified in provided measure bundle');
    }
    return { cqls, rootLibIdentifier, elmJSONs };
}
exports.extractLibrariesFromMeasureBundle = extractLibrariesFromMeasureBundle;
function extractMeasureFromBundle(measureBundle) {
    var _a;
    const measureEntry = (_a = measureBundle.entry) === null || _a === void 0 ? void 0 : _a.find(e => { var _a; return ((_a = e.resource) === null || _a === void 0 ? void 0 : _a.resourceType) === 'Measure'; });
    if (!measureEntry) {
        throw new CustomErrors_1.UnexpectedResource('Measure resource does not exist in provided measure bundle');
    }
    const measure = measureEntry.resource;
    if (!measure.library) {
        throw new CustomErrors_1.UnexpectedProperty(`Measure resource "Measure/${measure.id}" must specify a "library"`);
    }
    return measure;
}
exports.extractMeasureFromBundle = extractMeasureFromBundle;
/**
 * Detects missing ValueSets in the given measure bundle, retrieves them from VSAC, and adds
 * them as entries to the measure bundle.
 * @param {fhir4.Bundle} measureBundle the FHIR Bundle object containing the Measure resource
 * @param {CalculationOptions} options Options for calculation (may contain API key as an attribute)
 * @return {fhir4.Bundle} measure bundle with entries for missing ValueSets (fetched from VSAC)
 */
async function addValueSetsToMeasureBundle(measureBundle, options) {
    const missingVS = (0, ValueSetHelper_1.getMissingDependentValuesets)(measureBundle);
    if (missingVS.length > 0) {
        const valueSets = [];
        if (!options.vsAPIKey) {
            throw new CustomErrors_1.UnexpectedResource(`Missing the following valuesets: ${missingVS.join(', ')}, and no API key was provided to resolve them`);
        }
        const vsr = new ValueSetResolver_1.ValueSetResolver(options.vsAPIKey);
        const [expansions, errorMessages] = await vsr.getExpansionForValuesetUrls(missingVS);
        if (errorMessages.length > 0) {
            throw new Error(errorMessages.join('\n'));
        }
        valueSets.push(...expansions);
        const newBundle = measureBundle;
        valueSets.forEach(vs => {
            var _a;
            (_a = newBundle.entry) === null || _a === void 0 ? void 0 : _a.push({ resource: vs, request: { method: 'PUT', url: `ValueSet/${vs.id}` } });
        });
        return {
            results: newBundle
        };
    }
    // measure bundle is not missing any value sets
    return { results: measureBundle };
}
exports.addValueSetsToMeasureBundle = addValueSetsToMeasureBundle;
//# sourceMappingURL=MeasureBundleHelpers.js.map